import React, { useId, useState } from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import {
  isNamedPreset,
  parsePresetSelection,
  presetSelectionValue,
  writeNamedPreset,
  type PresetSelection,
} from "../utils/presetVocabulary"

interface PauseAnalysisConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

type PausePresetKey = "strict" | "normal" | "lenient" | "custom"

const DEFAULT_CUSTOM_MAD_Z_SCORE_THRESHOLD = 3.0

const PRESET_OPTIONS: Array<{
  key: PausePresetKey
  label: string
  description: string
}> = [
  {
    key: "strict",
    label: "Strict",
    description: "Flags more pauses as anomalies (z=2.0)",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Balanced sensitivity (z=3.0)",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags large outliers (z=4.0)",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own threshold",
  },
]

export const PauseAnalysisConfig: React.FC<PauseAnalysisConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const minPauseDurationId = useId()
  const metadata = metricRecordData.metric_metadata
  const canonicalPreset = Reflect.get(metadata, "preset")
  const rawPreset =
    canonicalPreset !== undefined
      ? canonicalPreset
      : metadata.pause_detection_preset
  const storedSelection = parsePresetSelection(rawPreset)
  const [currentSelection, setCurrentSelection] = useState<PresetSelection>(
    storedSelection.kind === "unknown"
      ? storedSelection
      : metadata.mad_z_score_threshold != null
        ? { kind: "custom" }
        : storedSelection,
  )
  const isCustom = currentSelection.kind === "custom"

  const handlePresetChange = (value: string) => {
    if (isNamedPreset(value)) {
      setCurrentSelection({ kind: "named", value })
      onChange({
        ...metricRecordData,
        metric_metadata: writeNamedPreset(
          metadata,
          "preset",
          "pause_detection_preset",
          value,
          ["mad_z_score_threshold"],
        ),
      })
      return
    }
    if (value !== "custom") {
      return
    }
    setCurrentSelection({ kind: "custom" })
    const base =
      storedSelection.kind === "named" ? storedSelection.value : "normal"
    const nextMetadata = {
      ...metadata,
      preset: base,
      mad_z_score_threshold:
        metadata.mad_z_score_threshold ?? DEFAULT_CUSTOM_MAD_Z_SCORE_THRESHOLD,
    }

    onChange({
      ...metricRecordData,
      metric_metadata: nextMetadata,
    })
  }

  const handleMinPauseDurationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_pause_duration_seconds: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMadZScoreThresholdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        mad_z_score_threshold: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMadZScoreThresholdBlur = () => {
    if (!isCustom) {
      return
    }
    const current = metadata.mad_z_score_threshold
    if (current == null) {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          mad_z_score_threshold: DEFAULT_CUSTOM_MAD_Z_SCORE_THRESHOLD,
        },
      })
    }
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label htmlFor={minPauseDurationId} className={LABEL_STYLES}>
            Minimum Pause Duration (seconds)
          </label>
        </div>
        <Input
          id={minPauseDurationId}
          type="number"
          step="0.1"
          min="0"
          value={
            metricRecordData.metric_metadata?.min_pause_duration_seconds ?? ""
          }
          onChange={handleMinPauseDurationChange}
          placeholder="1.0"
        />
        <p className="text-xs text-coval-text-muted">
          Pauses shorter than this duration will be ignored in the analysis.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Anomaly Detection Preset</span>
        </div>
        <Select
          value={presetSelectionValue(currentSelection)}
          onValueChange={handlePresetChange}
        >
          <SelectTrigger aria-label="Anomaly Detection Preset">
            <SelectValue placeholder="Select preset" />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
            {currentSelection.kind === "unknown" && (
              <SelectItem value="unknown" disabled>
                {currentSelection.label}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Controls how sensitive anomalous pause detection is. Choose Custom to
          set your own threshold.
        </p>
      </div>

      {isCustom && (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <span className={LABEL_STYLES}>MAD z-score Threshold</span>
            <Tooltip content="Pauses with a MAD-based modified z-score at or above this value are flagged as anomalous. Lower values flag more pauses.">
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <Input
            type="number"
            step="0.1"
            min="0"
            value={
              metricRecordData.metric_metadata?.mad_z_score_threshold ?? ""
            }
            onChange={handleMadZScoreThresholdChange}
            onBlur={handleMadZScoreThresholdBlur}
            placeholder="3.0"
            aria-label="MAD z-score Threshold"
          />
        </div>
      )}
    </div>
  )
}
